import React, { useEffect, useState, useMemo } from 'react';
import { db } from '@archicore/shared';
import { collection, getDocs } from 'firebase/firestore';
import { 
  BarChart3, Users, Target, Globe, Smartphone, 
  Monitor, Tablet, RefreshCw, Download, Search, 
  Sparkles, Clock, Eye, Flame, BookOpen, Layers,
  Compass, ArrowRight, ExternalLink, MapPin,
  Bot, UserCheck, Shield, Copy, Check
} from 'lucide-react';

export interface LandingVisitRecord {
  id: string;
  visitorId: string;
  sessionId: string;
  isNewVisitor: boolean;
  channel: 'facebook' | 'instagram' | 'whatsapp' | 'google' | 'linkedin' | 'tiktok' | 'youtube' | 'twitter' | 'referral' | 'direct';
  channelLabel: string;
  referrer?: string;
  referrerDomain?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  device: 'mobile' | 'tablet' | 'desktop';
  browser: string;
  os: string;
  landingPath: string;
  screenResolution?: string;
  language?: string;
  isDev?: boolean;
  isBot?: boolean;
  isTeam?: boolean;
  dateStr?: string;
  timestamp?: any;
  // Métricas de Dwell Time y Navegación
  pageTimes?: Record<string, number>;
  sectionTimes?: Record<string, number>;
  pagesVisited?: string[];
  totalDwellSeconds?: number;
  favoriteSection?: string;
  favoritePage?: string;
  // Métricas de Geolocalización
  city?: string;
  region?: string;
  country?: string;
  countryCode?: string;
}

interface SalesLeadRecord {
  id: string;
  createdAt?: any;
}

const SECTION_METADATA: Record<string, { label: string; icon: string; color: string }> = {
  presupuestos: { label: 'Presupuestos Paramétricos & APU (BIM 5D)', icon: '📐', color: 'bg-indigo-600' },
  gastos: { label: 'Gastos de Obra & OCR de Tickets', icon: '🧾', color: 'bg-emerald-600' },
  asistencia: { label: 'Control de Asistencia Biométrico & Nómina', icon: '👷', color: 'bg-cyan-600' },
  inventario: { label: 'Control de Inventario & Herramientas', icon: '📦', color: 'bg-amber-600' },
  core_ai: { label: 'IA Core Copiloto de Inteligencia', icon: '🤖', color: 'bg-purple-600' },
  beneficios: { label: 'Beneficios Cuantificables & ROI', icon: '📈', color: 'bg-blue-600' },
  hero: { label: 'Hero / Propuesta de Valor', icon: '🚀', color: 'bg-slate-700' },
  cta_final: { label: 'Llamado a la Acción / Registro Beta', icon: '🎯', color: 'bg-orange-600' }
};

const PAGE_METADATA: Record<string, { label: string; icon: string }> = {
  '/': { label: 'Inicio / Landing Principal', icon: '🏠' },
  '/planes': { label: 'Planes y Precios', icon: '💎' },
  '/contact': { label: 'Contacto / Hablar con Asesor', icon: '📩' },
  '/novedades': { label: 'Novedades y Changelog', icon: '🚀' },
  '/terminos': { label: 'Términos de Servicio', icon: '📄' },
  '/privacidad': { label: 'Política de Privacidad', icon: '🔒' }
};

function formatDuration(sec: number): string {
  if (!sec || sec <= 0) return '0s';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function getCountryFlag(countryCode?: string): string {
  if (!countryCode) return '🌐';
  const code = countryCode.trim().toUpperCase();
  if (code === 'MX') return '🇲🇽';
  if (code === 'CA') return '🇨🇦';
  if (code === 'US') return '🇺🇸';
  if (code === 'ES') return '🇪🇸';
  if (code === 'CO') return '🇨🇴';
  if (code === 'AR') return '🇦🇷';
  if (code === 'CL') return '🇨🇱';
  if (code.length === 2) {
    const offset = 127397;
    const chars = [...code].map(c => c.charCodeAt(0) + offset);
    return String.fromCodePoint(...chars);
  }
  return '🌐';
}

export default function LandingTrafficTab() {
  const [visits, setVisits] = useState<LandingVisitRecord[]>([]);
  const [leads, setLeads] = useState<SalesLeadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filtros
  const [dateRange, setDateRange] = useState<'today' | '7d' | '30d' | 'all'>('7d');
  const [hideDev, setHideDev] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filtros de Auditoría y Audiencia
  const [visitorType, setVisitorType] = useState<'humans' | 'bots' | 'all'>('humans');
  const [hideTeam, setHideTeam] = useState<boolean>(true);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  // Heurística de detección de bots y scrapers (nuevos y registros históricos)
  const isBotVisit = (v: LandingVisitRecord): boolean => {
    if (v.isBot !== undefined) return v.isBot;
    // Heurística retroactiva para registros históricos sin etiqueta explícita
    const city = (v.city || '').toLowerCase();
    const ch = (v.channelLabel || '').toLowerCase();
    const br = (v.browser || '').toLowerCase();
    const dwell = v.totalDwellSeconds || 0;
    // Servidores conocidos de datacenters (Microsoft Azure en Quincy / Chase City / Boydton, AWS Ashburn)
    if (city === 'quincy' || city === 'chase city' || city === 'boydton' || city === 'ashburn') return true;
    if (dwell === 0 && (br === 'otro' || /bot|crawl|spider|preview|dubgtcg|python|curl|scraper|scanner/i.test(ch) || /bot|crawl|spider|preview|headless/i.test(br))) return true;
    return false;
  };

  const handleCopyTeamLink = () => {
    const teamUrl = 'https://archicoreanalytic.com/?equipo=activar';
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(teamUrl);
    } else {
      const input = document.createElement('input');
      input.value = teamUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 4000);
  };

  // Carga de Datos
  const loadData = async () => {
    setLoading(true);
    try {
      const visitsRef = collection(db, 'landing_visits');
      const visitsSnap = await getDocs(visitsRef);
      const items: LandingVisitRecord[] = visitsSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as LandingVisitRecord));

      // Ordenar por fecha descendente
      items.sort((a, b) => {
        const tA = a.timestamp?.seconds || (a.timestamp?.toDate ? a.timestamp.toDate().getTime() / 1000 : 0);
        const tB = b.timestamp?.seconds || (b.timestamp?.toDate ? b.timestamp.toDate().getTime() / 1000 : 0);
        return tB - tA;
      });

      setVisits(items);

      // Cargar Leads de Ventas para tasa de conversión
      const leadsRef = collection(db, 'sales_leads');
      const leadsSnap = await getDocs(leadsRef);
      const leadItems: SalesLeadRecord[] = leadsSnap.docs.map(d => ({
        id: d.id,
        createdAt: d.data().createdAt
      }));
      setLeads(leadItems);
    } catch (err) {
      console.error('Error al cargar métricas de tráfico:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  // Filtrado temporal y de audiencia
  const filteredVisits = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    return visits.filter(v => {
      // Filtro desarrollo
      if (hideDev && v.isDev) return false;

      // Filtro equipo interno / familia
      if (hideTeam && (v.isTeam || v.isDev)) return false;

      // Filtro Tipo de Visitante (Humanos vs Bots)
      const isBot = isBotVisit(v);
      if (visitorType === 'humans' && isBot) return false;
      if (visitorType === 'bots' && !isBot) return false;

      // Filtro fecha
      if (dateRange === 'today') {
        const vDate = v.dateStr || (v.timestamp?.toDate ? v.timestamp.toDate().toISOString().split('T')[0] : '');
        if (vDate !== todayStr) return false;
      } else if (dateRange === '7d') {
        const vTime = v.timestamp?.seconds ? v.timestamp.seconds * 1000 : (v.timestamp?.toDate ? v.timestamp.toDate().getTime() : 0);
        if (vTime && vTime < sevenDaysAgo.getTime()) return false;
      } else if (dateRange === '30d') {
        const vTime = v.timestamp?.seconds ? v.timestamp.seconds * 1000 : (v.timestamp?.toDate ? v.timestamp.toDate().getTime() : 0);
        if (vTime && vTime < thirtyDaysAgo.getTime()) return false;
      }

      // Filtro canal
      if (selectedChannel !== 'all' && v.channel !== selectedChannel) {
        return false;
      }

      // Filtro búsqueda
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchChannel = v.channelLabel?.toLowerCase().includes(term);
        const matchCampaign = v.utmCampaign?.toLowerCase().includes(term);
        const matchSource = v.utmSource?.toLowerCase().includes(term);
        const matchRef = v.referrer?.toLowerCase().includes(term);
        const matchBrowser = v.browser?.toLowerCase().includes(term);
        const matchOs = v.os?.toLowerCase().includes(term);
        const matchPath = v.landingPath?.toLowerCase().includes(term);
        const matchFavorite = v.favoriteSection?.toLowerCase().includes(term);
        const matchCity = v.city?.toLowerCase().includes(term);
        const matchRegion = v.region?.toLowerCase().includes(term);
        const matchCountry = v.country?.toLowerCase().includes(term);
        if (!matchChannel && !matchCampaign && !matchSource && !matchRef && !matchBrowser && !matchOs && !matchPath && !matchFavorite && !matchCity && !matchRegion && !matchCountry) {
          return false;
        }
      }

      return true;
    });
  }, [visits, dateRange, hideDev, hideTeam, visitorType, selectedChannel, searchTerm]);

  // Conteo de Humanos y Bots en el rango temporal seleccionado (para insignias de filtro)
  const audienceCounts = useMemo(() => {
    let humans = 0;
    let bots = 0;
    let team = 0;

    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    visits.forEach(v => {
      if (hideDev && v.isDev) return;
      if (dateRange === 'today') {
        const vDate = v.dateStr || (v.timestamp?.toDate ? v.timestamp.toDate().toISOString().split('T')[0] : '');
        if (vDate !== todayStr) return;
      } else if (dateRange === '7d') {
        const vTime = v.timestamp?.seconds ? v.timestamp.seconds * 1000 : (v.timestamp?.toDate ? v.timestamp.toDate().getTime() : 0);
        if (vTime && vTime < sevenDaysAgo.getTime()) return;
      } else if (dateRange === '30d') {
        const vTime = v.timestamp?.seconds ? v.timestamp.seconds * 1000 : (v.timestamp?.toDate ? v.timestamp.toDate().getTime() : 0);
        if (vTime && vTime < thirtyDaysAgo.getTime()) return;
      }

      if (v.isTeam) team++;
      if (isBotVisit(v)) bots++;
      else humans++;
    });

    return { humans, bots, team, total: humans + bots };
  }, [visits, dateRange, hideDev]);

  // Métricas agregadas y analítica de páginas/secciones
  const stats = useMemo(() => {
    const totalVisits = filteredVisits.length;
    const uniqueVisitors = new Set(filteredVisits.map(v => v.visitorId)).size;
    const newVisitorsCount = filteredVisits.filter(v => v.isNewVisitor).length;

    // 1. Conteo por canal
    const channelCounts: Record<string, { count: number; label: string }> = {};
    filteredVisits.forEach(v => {
      const ch = v.channel || 'direct';
      if (!channelCounts[ch]) {
        channelCounts[ch] = { count: 0, label: v.channelLabel || ch };
      }
      channelCounts[ch].count++;
    });

    const channelList = Object.entries(channelCounts)
      .map(([channel, data]) => ({
        channel,
        label: data.label,
        count: data.count,
        percentage: totalVisits > 0 ? Math.round((data.count / totalVisits) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);

    const topChannel = channelList[0] || { label: 'Sin datos', percentage: 0 };

    // 2. Conteo por dispositivo
    let mobileCount = 0;
    let desktopCount = 0;
    let tabletCount = 0;
    filteredVisits.forEach(v => {
      if (v.device === 'mobile') mobileCount++;
      else if (v.device === 'tablet') tabletCount++;
      else desktopCount++;
    });

    // 3. Conteo por campaña UTM
    const campaignCounts: Record<string, number> = {};
    filteredVisits.forEach(v => {
      if (v.utmCampaign) {
        campaignCounts[v.utmCampaign] = (campaignCounts[v.utmCampaign] || 0) + 1;
      }
    });

    const campaignList = Object.entries(campaignCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // 4. Histograma de 14 días
    const dailyMap: Record<string, { total: number; uniques: Set<string> }> = {};
    const last14Days: string[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const str = d.toISOString().split('T')[0];
      last14Days.push(str);
      dailyMap[str] = { total: 0, uniques: new Set() };
    }

    filteredVisits.forEach(v => {
      const d = v.dateStr || (v.timestamp?.toDate ? v.timestamp.toDate().toISOString().split('T')[0] : '');
      if (d && dailyMap[d]) {
        dailyMap[d].total++;
        if (v.visitorId) dailyMap[d].uniques.add(v.visitorId);
      }
    });

    const dailyChartData = last14Days.map(dateStr => {
      const dayData = dailyMap[dateStr] || { total: 0, uniques: new Set() };
      return {
        dateStr,
        label: dateStr.split('-').slice(1).join('/'),
        total: dayData.total,
        uniques: dayData.uniques.size
      };
    });

    const maxDayCount = Math.max(...dailyChartData.map(d => d.total), 5);

    // 5. Tasa de conversión a leads
    const conversionRate = uniqueVisitors > 0 
      ? ((leads.length / uniqueVisitors) * 100).toFixed(1)
      : '0.0';

    // 6. TIEMPO PROMEDIO EN SITIO (Dwell Time Global)
    let totalDwellSum = 0;
    let visitsWithDwell = 0;
    filteredVisits.forEach(v => {
      if (v.totalDwellSeconds && v.totalDwellSeconds > 0) {
        totalDwellSum += v.totalDwellSeconds;
        visitsWithDwell++;
      }
    });
    const avgDwellTimeSeconds = visitsWithDwell > 0 ? Math.round(totalDwellSum / visitsWithDwell) : 0;

    // 7. RANKING DE PÁGINAS MÁS VISITADAS (Pageviews)
    const pageAggregates: Record<string, { visitsCount: number; totalSeconds: number }> = {};
    filteredVisits.forEach(v => {
      const pages = v.pagesVisited && v.pagesVisited.length > 0 ? v.pagesVisited : [v.landingPath || '/'];
      pages.forEach(p => {
        const cleanPath = p.split('?')[0] || '/';
        if (!pageAggregates[cleanPath]) {
          pageAggregates[cleanPath] = { visitsCount: 0, totalSeconds: 0 };
        }
        pageAggregates[cleanPath].visitsCount++;
      });

      if (v.pageTimes) {
        Object.entries(v.pageTimes).forEach(([p, sec]) => {
          const cleanPath = p.split('?')[0] || '/';
          if (!pageAggregates[cleanPath]) {
            pageAggregates[cleanPath] = { visitsCount: 1, totalSeconds: 0 };
          }
          pageAggregates[cleanPath].totalSeconds += sec;
        });
      }
    });

    const pageRanking = Object.entries(pageAggregates)
      .map(([path, data]) => {
        const meta = PAGE_METADATA[path] || { label: path, icon: '📄' };
        const avgSeconds = data.visitsCount > 0 ? Math.round(data.totalSeconds / data.visitsCount) : 0;
        const percentage = totalVisits > 0 ? Math.round((data.visitsCount / totalVisits) * 100) : 0;
        return {
          path,
          label: meta.label,
          icon: meta.icon,
          visitsCount: data.visitsCount,
          percentage,
          avgSeconds,
          totalSeconds: data.totalSeconds
        };
      })
      .sort((a, b) => b.visitsCount - a.visitsCount);

    // 8. MAPA DE ATENCIÓN POR SECCIONES (Dwell Time por Módulo)
    const sectionAggregates: Record<string, { totalSeconds: number; visitorsCount: number }> = {};
    Object.keys(SECTION_METADATA).forEach(secKey => {
      sectionAggregates[secKey] = { totalSeconds: 0, visitorsCount: 0 };
    });

    filteredVisits.forEach(v => {
      if (v.sectionTimes) {
        Object.entries(v.sectionTimes).forEach(([sec, secTime]) => {
          if (secTime > 0) {
            if (!sectionAggregates[sec]) {
              sectionAggregates[sec] = { totalSeconds: 0, visitorsCount: 0 };
            }
            sectionAggregates[sec].totalSeconds += secTime;
            sectionAggregates[sec].visitorsCount++;
          }
        });
      }
    });

    let totalSectionSecondsAll = 0;
    Object.values(sectionAggregates).forEach(s => (totalSectionSecondsAll += s.totalSeconds));

    const sectionRanking = Object.entries(sectionAggregates)
      .map(([secKey, data]) => {
        const meta = SECTION_METADATA[secKey] || { label: secKey, icon: '📌', color: 'bg-slate-600' };
        const avgSec = data.visitorsCount > 0 ? Math.round(data.totalSeconds / data.visitorsCount) : 0;
        const attentionShare = totalSectionSecondsAll > 0 ? Math.round((data.totalSeconds / totalSectionSecondsAll) * 100) : 0;
        return {
          key: secKey,
          label: meta.label,
          icon: meta.icon,
          color: meta.color,
          totalSeconds: data.totalSeconds,
          visitorsCount: data.visitorsCount,
          avgSec,
          attentionShare
        };
      })
      .sort((a, b) => b.totalSeconds - a.totalSeconds);

    const topSection = sectionRanking[0] || {
      key: 'none',
      label: 'Sin datos',
      icon: '✨',
      color: 'bg-slate-600',
      totalSeconds: 0,
      visitorsCount: 0,
      avgSec: 0,
      attentionShare: 0
    };

    // 9. MAPA GEOGRÁFICO DE PROSPECTOS (Ciudades y Países)
    const cityAggregates: Record<string, { count: number; region: string; country: string; countryCode: string }> = {};
    const countryAggregates: Record<string, { count: number; countryCode: string }> = {};

    filteredVisits.forEach(v => {
      const cityName = v.city && v.city.trim() !== '' ? v.city.trim() : 'Ubicación Desconocida';
      const countryName = v.country && v.country.trim() !== '' ? v.country.trim() : 'Desconocido';
      const countryCode = v.countryCode || '';
      const regionName = v.region || '';

      if (!cityAggregates[cityName]) {
        cityAggregates[cityName] = { count: 0, region: regionName, country: countryName, countryCode };
      }
      cityAggregates[cityName].count++;

      if (!countryAggregates[countryName]) {
        countryAggregates[countryName] = { count: 0, countryCode };
      }
      countryAggregates[countryName].count++;
    });

    const cityRanking = Object.entries(cityAggregates)
      .map(([cityName, data]) => ({
        cityName,
        region: data.region,
        country: data.country,
        countryCode: data.countryCode,
        count: data.count,
        percentage: totalVisits > 0 ? Math.round((data.count / totalVisits) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);

    const countryRanking = Object.entries(countryAggregates)
      .map(([countryName, data]) => ({
        countryName,
        countryCode: data.countryCode,
        count: data.count,
        percentage: totalVisits > 0 ? Math.round((data.count / totalVisits) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);

    const topCity = cityRanking.find(c => c.cityName !== 'Ubicación Desconocida') || null;

    return {
      totalVisits,
      uniqueVisitors,
      newVisitorsCount,
      topChannel,
      channelList,
      mobileCount,
      desktopCount,
      tabletCount,
      campaignList,
      dailyChartData,
      maxDayCount,
      conversionRate,
      totalLeads: leads.length,
      avgDwellTimeSeconds,
      pageRanking,
      sectionRanking,
      topSection,
      totalSectionSecondsAll,
      cityRanking,
      countryRanking,
      topCity
    };
  }, [filteredVisits, leads]);

  // Exportar a CSV con las nuevas columnas de atención y geolocalización
  const handleExportCSV = () => {
    if (!filteredVisits.length) {
      alert('No hay datos de visitas para exportar.');
      return;
    }

    const headers = [
      'ID',
      'Fecha',
      'Hora',
      'Ciudad',
      'Región/Estado',
      'País',
      'Canal',
      'Origen/Referrer',
      'Campaña UTM',
      'Dispositivo',
      'SO',
      'Ruta Principal',
      'Páginas Visitadas',
      'Tiempo en Sitio (seg)',
      'Tiempo Formateado',
      'Sección Favorita',
      'Nuevo Visitante'
    ];

    const rows = filteredVisits.map(v => {
      let dateStr = v.dateStr || '';
      let timeStr = '';
      if (v.timestamp?.toDate) {
        const d = v.timestamp.toDate();
        dateStr = d.toLocaleDateString('es-MX');
        timeStr = d.toLocaleTimeString('es-MX');
      }

      const pagesStr = (v.pagesVisited || [v.landingPath || '/']).join(' > ');
      const dwellSec = v.totalDwellSeconds || 0;
      const dwellFormatted = formatDuration(dwellSec);
      const favSec = SECTION_METADATA[v.favoriteSection || '']?.label || v.favoriteSection || 'hero';

      return [
        v.id,
        dateStr,
        timeStr,
        `"${v.city || 'Desconocida'}"`,
        `"${v.region || ''}"`,
        `"${v.country || ''}"`,
        `"${v.channelLabel || v.channel}"`,
        `"${v.referrer || 'Direct'}"`,
        `"${v.utmCampaign || ''}"`,
        v.device,
        v.os,
        `"${v.landingPath}"`,
        `"${pagesStr}"`,
        dwellSec,
        `"${dwellFormatted}"`,
        `"${favSec}"`,
        v.isNewVisitor ? 'Sí' : 'No'
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `analitica_landing_archicore_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getChannelBadge = (channel: string, label: string) => {
    const displayLabel = (label || '').replace(/^Campaña\s*\(/i, 'Origen (');
    switch (channel) {
      case 'facebook':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
            Facebook
          </span>
        );
      case 'instagram':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-pink-50 text-pink-700 border border-pink-200">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-600"></span>
            Instagram
          </span>
        );
      case 'whatsapp':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
            WhatsApp
          </span>
        );
      case 'google':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
            Google
          </span>
        );
      case 'linkedin':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-600"></span>
            LinkedIn
          </span>
        );
      case 'direct':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
            Directo
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-cyan-50 text-cyan-800 border border-cyan-200">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-600"></span>
            {displayLabel}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Barra Superior de Control y Filtros */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3.5">
        {/* Fila 1: Rango Temporal y Búsqueda */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
          {/* Selector de Rango Temporal */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
            <button
              onClick={() => setDateRange('today')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                dateRange === 'today'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Hoy
            </button>
            <button
              onClick={() => setDateRange('7d')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                dateRange === '7d'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Últimos 7 días
            </button>
            <button
              onClick={() => setDateRange('30d')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                dateRange === '30d'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Últimos 30 días
            </button>
            <button
              onClick={() => setDateRange('all')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                dateRange === 'all'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Histórico Total
            </button>
          </div>

          {/* Buscador y Controles Rápidos */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 sm:w-60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por origen, módulo o ruta..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-slate-50/50"
              />
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl border border-slate-200 transition-all"
              title="Actualizar datos en vivo"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin text-cyan-600' : ''} />
            </button>

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
            >
              <Download size={14} />
              CSV
            </button>
          </div>
        </div>

        {/* Fila 2: Segmentación de Audiencia (Humanos vs Bots) y Control de Equipo */}
        <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Selector de Tipo de Visitante */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
            <button
              onClick={() => setVisitorType('humans')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                visitorType === 'humans'
                  ? 'bg-white text-emerald-700 shadow-xs border border-emerald-200/60'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <UserCheck size={14} className={visitorType === 'humans' ? 'text-emerald-600' : 'text-slate-400'} />
              <span>Personas Reales ({audienceCounts.humans})</span>
            </button>
            <button
              onClick={() => setVisitorType('bots')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                visitorType === 'bots'
                  ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Bot size={14} className={visitorType === 'bots' ? 'text-cyan-600' : 'text-slate-400'} />
              <span>Bots & Servidores ({audienceCounts.bots})</span>
            </button>
            <button
              onClick={() => setVisitorType('all')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                visitorType === 'all'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Globe size={14} className={visitorType === 'all' ? 'text-blue-600' : 'text-slate-400'} />
              <span>Todo el Tráfico ({audienceCounts.total})</span>
            </button>
          </div>

          {/* Filtros de Equipo y Dev + Copiar Enlace */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-600 select-none bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={hideTeam}
                onChange={e => setHideTeam(e.target.checked)}
                className="rounded text-purple-600 focus:ring-purple-500"
              />
              <Shield size={13} className="text-purple-600" />
              <span>Ocultar Equipo</span>
            </label>

            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-600 select-none bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={hideDev}
                onChange={e => setHideDev(e.target.checked)}
                className="rounded text-cyan-600 focus:ring-cyan-500"
              />
              <span>Ocultar Dev</span>
            </label>

            <button
              onClick={handleCopyTeamLink}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border shadow-xs ${
                copiedLink 
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300 ring-2 ring-emerald-400/20' 
                  : 'bg-purple-50 hover:bg-purple-100 text-purple-800 border-purple-200'
              }`}
              title="Copia el enlace para WhatsApp. Tu familia o equipo solo debe abrirlo una vez en sus laptops/celulares."
            >
              {copiedLink ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} className="text-purple-600" />}
              <span>{copiedLink ? '¡Enlace copiado!' : 'Copiar Enlace Familia'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 5 Tarjetas KPI Ejecutivas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* KPI 1: Visitas Totales */}
        <div className="p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
          <div className="flex justify-between items-start mb-2">
            <div className="p-2 bg-cyan-50 text-cyan-600 border border-cyan-100 rounded-xl">
              <Eye size={18} />
            </div>
            <span className="text-[10px] bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              {dateRange === 'today' ? 'Hoy' : dateRange === '7d' ? '7 Días' : dateRange === '30d' ? '30 Días' : 'Total'}
            </span>
          </div>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Visitas Totales</p>
          <h4 className="text-2xl font-black text-slate-900 mt-0.5">
            {stats.totalVisits.toLocaleString()}
          </h4>
          <p className="text-[11px] text-slate-500 mt-1 font-medium">
            Sesiones en el sitio público
          </p>
        </div>

        {/* KPI 2: Visitantes Únicos */}
        <div className="p-4 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white shadow-xs">
          <div className="flex justify-between items-start mb-2">
            <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-xs">
              <Users size={18} />
            </div>
            <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Audiencia Real
            </span>
          </div>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Visitantes Únicos</p>
          <h4 className="text-2xl font-black text-slate-900 mt-0.5">
            {stats.uniqueVisitors.toLocaleString()}
          </h4>
          <p className="text-[11px] text-indigo-700 mt-1 font-semibold">
            {stats.newVisitorsCount} ({stats.totalVisits > 0 ? Math.round((stats.newVisitorsCount / stats.totalVisits) * 100) : 0}%) nuevos
          </p>
        </div>

        {/* KPI 3: TIEMPO PROMEDIO EN SITIO (Dwell Time) */}
        <div className="p-4 rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50/50 to-white shadow-xs">
          <div className="flex justify-between items-start mb-2">
            <div className="p-2 bg-purple-600 text-white rounded-xl shadow-xs">
              <Clock size={18} />
            </div>
            <span className="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Retención
            </span>
          </div>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Tiempo Promedio</p>
          <h4 className="text-2xl font-black text-purple-900 mt-0.5">
            {formatDuration(stats.avgDwellTimeSeconds)}
          </h4>
          <p className="text-[11px] text-purple-700 mt-1 font-semibold truncate" title={stats.topSection.label}>
            Top: {stats.topSection.icon} {stats.topSection.label.split(' ')[0]}
          </p>
        </div>

        {/* KPI 4: Tasa de Conversión a Leads */}
        <div className="p-4 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white shadow-xs">
          <div className="flex justify-between items-start mb-2">
            <div className="p-2 bg-emerald-600 text-white rounded-xl shadow-xs">
              <Target size={18} />
            </div>
            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Conversión
            </span>
          </div>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Visitas a Prospectos</p>
          <h4 className="text-2xl font-black text-emerald-700 mt-0.5">
            {stats.conversionRate}%
          </h4>
          <p className="text-[11px] text-slate-600 mt-1 font-medium">
            {stats.totalLeads} contactos generados
          </p>
        </div>

        {/* KPI 5: Canal Líder de Tráfico */}
        <div className="p-4 rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50/50 to-white shadow-xs">
          <div className="flex justify-between items-start mb-2">
            <div className="p-2 bg-orange-500 text-white rounded-xl shadow-xs">
              <Sparkles size={18} />
            </div>
            <span className="text-[10px] bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
              Top Canal
            </span>
          </div>
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Fuente #1</p>
          <h4 className="text-xl font-black text-slate-900 mt-0.5 truncate">
            {stats.topChannel.label}
          </h4>
          <p className="text-[11px] text-orange-700 mt-1 font-semibold">
            {stats.topChannel.percentage}% del tráfico
          </p>
        </div>
      </div>

      {/* BLOQUE DE INTELIGENCIA DE CONTENIDOS: Páginas Más Visitadas & Mapa de Atención */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Columna A: Ranking de Páginas Más Visitadas */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <BookOpen size={18} className="text-cyan-600" />
                  Páginas Públicas Más Visitadas
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Flujo de navegación entre Landing, Planes, Contacto y Novedades
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                {stats.pageRanking.length} rutas
              </span>
            </div>

            <div className="space-y-3">
              {stats.pageRanking.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  Sin páginas registradas aún.
                </div>
              ) : (
                stats.pageRanking.map((p, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-slate-50/70 border border-slate-100 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{p.icon}</span>
                        <div>
                          <p className="font-bold text-xs text-slate-800">{p.label}</p>
                          <p className="text-[10px] font-mono text-slate-400">{p.path}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-mono font-bold text-xs text-slate-900">
                          {p.visitsCount} <span className="text-[10px] text-slate-400 font-normal">visitas ({p.percentage}%)</span>
                        </span>
                        <p className="text-[10px] font-semibold text-purple-700">
                          ⏱️ Promedio: {formatDuration(p.avgSeconds)}
                        </p>
                      </div>
                    </div>
                    {/* Barra de Proporción */}
                    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-cyan-600 rounded-full transition-all duration-500"
                        style={{ width: `${p.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between mt-3">
            <span>Rastreo 100% aislado a visitantes de marketing</span>
            <span className="font-medium text-slate-600">Total páginas vistas: {stats.pageRanking.reduce((acc, r) => acc + r.visitsCount, 0)}</span>
          </div>
        </div>

        {/* Columna B: Mapa de Atención por Módulo (Tiempo en Secciones) */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <Flame size={18} className="text-orange-500" />
                  Mapa de Atención por Módulo (Landing Page)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  ¿Cuánto tiempo se queda cada persona leyendo cada sección?
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                Sensor de Visión Activo
              </span>
            </div>

            {/* Banner de Módulo Estrella */}
            {stats.topSection.totalSeconds > 0 && (
              <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-transparent border border-orange-200 flex items-center gap-3">
                <span className="text-2xl">{stats.topSection.icon}</span>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-orange-800">
                    Módulo con Mayor Interés Comercial
                  </p>
                  <h4 className="text-xs font-bold text-slate-900">
                    {stats.topSection.label}
                  </h4>
                  <p className="text-[10px] text-slate-600">
                    Retiene el <strong>{stats.topSection.attentionShare}%</strong> de la atención total con un promedio de <strong>{formatDuration(stats.topSection.avgSec)}</strong> por lectura.
                  </p>
                </div>
              </div>
            )}

            {/* Barras de Secciones Ordenadas por Tiempo */}
            <div className="space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
              {stats.sectionRanking.map((sec) => (
                <div key={sec.key} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-700 flex items-center gap-1.5 truncate">
                      <span>{sec.icon}</span>
                      <span className="truncate max-w-[220px]">{sec.label}</span>
                    </span>
                    <div className="text-right shrink-0">
                      <span className="font-mono font-bold text-slate-900">
                        {formatDuration(sec.avgSec)} <span className="text-[10px] text-slate-400 font-normal">({sec.attentionShare}%)</span>
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${sec.color}`}
                      style={{ width: `${sec.attentionShare}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between mt-3">
            <span>Pausado automático al cambiar de pestaña o inactividad</span>
            <span className="font-medium text-slate-600">Total tiempo auditado: {formatDuration(stats.totalSectionSecondsAll)}</span>
          </div>
        </div>
      </div>

      {/* Gráfica de Tendencia Diaria (Histograma) y Distribución por Canal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Histograma de Visitas (2 Columnas) */}
        <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <BarChart3 size={18} className="text-cyan-600" />
                  Tendencia Diaria de Visitas (Últimos 14 días)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Distribución de tráfico diario y visitantes únicos
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs font-semibold">
                <div className="flex items-center gap-1.5 text-slate-600">
                  <span className="w-3 h-3 rounded-sm bg-cyan-500"></span>
                  Totales
                </div>
                <div className="flex items-center gap-1.5 text-slate-600">
                  <span className="w-3 h-3 rounded-sm bg-indigo-600"></span>
                  Únicos
                </div>
              </div>
            </div>

            {/* Barras del Histograma */}
            <div className="h-48 flex items-end justify-between gap-1 sm:gap-2 pt-6 pb-2 border-b border-slate-100">
              {stats.dailyChartData.map((day, idx) => {
                const totalHeightPct = stats.maxDayCount > 0 ? (day.total / stats.maxDayCount) * 100 : 0;
                const uniqueHeightPct = stats.maxDayCount > 0 ? (day.uniques / stats.maxDayCount) * 100 : 0;

                return (
                  <div key={idx} className="flex-1 flex flex-col items-center h-full justify-end group relative">
                    <div className="absolute -top-12 bg-slate-900 text-white text-[10px] py-1 px-2 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                      <p className="font-bold">{day.dateStr}</p>
                      <p>{day.total} visitas • {day.uniques} únicos</p>
                    </div>

                    <div className="w-full max-w-[28px] flex items-end justify-center gap-0.5 h-full">
                      <div
                        className="w-1/2 bg-cyan-500 hover:bg-cyan-600 rounded-t-sm transition-all duration-300 shadow-xs"
                        style={{ height: `${Math.max(totalHeightPct, 4)}%` }}
                      />
                      <div
                        className="w-1/2 bg-indigo-600 hover:bg-indigo-700 rounded-t-sm transition-all duration-300 shadow-xs"
                        style={{ height: `${Math.max(uniqueHeightPct, 4)}%` }}
                      />
                    </div>

                    <span className="text-[9px] font-mono text-slate-400 mt-2 rotate-[-45deg] origin-top-left sm:rotate-0 sm:origin-center">
                      {day.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-slate-500 pt-3">
            <span>Pico máximo: <strong className="text-slate-800">{stats.maxDayCount} visitas/día</strong></span>
            <span>Promedio diario: <strong className="text-slate-800">{Math.round(stats.totalVisits / 14)} visitas</strong></span>
          </div>
        </div>

        {/* Desglose de Fuentes de Tráfico (1 Columna) */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <Globe size={18} className="text-indigo-600" />
                Fuentes & Canales
              </h3>
              <span className="text-xs text-slate-500">{stats.channelList.length} canales</span>
            </div>

            <div className="space-y-3">
              {stats.channelList.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  Sin visitas registradas en este período.
                </div>
              ) : (
                stats.channelList.map((ch, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                        {ch.label}
                      </span>
                      <span className="font-mono font-bold text-slate-900">
                        {ch.count} <span className="text-[10px] text-slate-400 font-normal">({ch.percentage}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          ch.channel === 'facebook'
                            ? 'bg-blue-600'
                            : ch.channel === 'instagram'
                            ? 'bg-pink-600'
                            : ch.channel === 'whatsapp'
                            ? 'bg-emerald-500'
                            : ch.channel === 'google'
                            ? 'bg-amber-500'
                            : ch.channel === 'direct'
                            ? 'bg-slate-500'
                            : 'bg-cyan-500'
                        }`}
                        style={{ width: `${ch.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Dispositivos (Móvil vs PC) */}
          <div className="pt-4 border-t border-slate-100 mt-4">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Dispositivos</h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                <Smartphone size={16} className="mx-auto text-slate-600 mb-1" />
                <span className="text-[10px] font-medium text-slate-500 block">Móvil</span>
                <span className="text-xs font-black text-slate-900">
                  {stats.totalVisits > 0 ? Math.round((stats.mobileCount / stats.totalVisits) * 100) : 0}%
                </span>
              </div>
              <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                <Monitor size={16} className="mx-auto text-slate-600 mb-1" />
                <span className="text-[10px] font-medium text-slate-500 block">PC</span>
                <span className="text-xs font-black text-slate-900">
                  {stats.totalVisits > 0 ? Math.round((stats.desktopCount / stats.totalVisits) * 100) : 0}%
                </span>
              </div>
              <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                <Tablet size={16} className="mx-auto text-slate-600 mb-1" />
                <span className="text-[10px] font-medium text-slate-500 block">Tablet</span>
                <span className="text-xs font-black text-slate-900">
                  {stats.totalVisits > 0 ? Math.round((stats.tabletCount / stats.totalVisits) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BLOQUE DE INTELIGENCIA GEOGRÁFICA: Ciudades & Mercados Clave */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ciudades y Zonas Más Activas (2 Columnas) */}
        <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <MapPin size={18} className="text-cyan-600" />
                  Ciudades & Zonas de Mayor Interés Comercial
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Detección geográfica por IP de prospectos (Tulum, Cancún, Riviera Maya, Monterrey, CDMX, etc.)
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">
                {stats.cityRanking.filter(c => c.cityName !== 'Ubicación Desconocida').length} ciudades
              </span>
            </div>

            {/* Banner de Ciudad o Zona Estrella */}
            {stats.topCity && (
              <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-transparent border border-cyan-200 flex items-center gap-3">
                <span className="text-2xl">{getCountryFlag(stats.topCity.countryCode)}</span>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-800">
                    Mercado Local con Mayor Interés
                  </p>
                  <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                    <span>{stats.topCity.cityName}</span>
                    {stats.topCity.region && <span className="text-slate-500 font-normal">({stats.topCity.region})</span>}
                  </h4>
                  <p className="text-[10px] text-slate-600">
                    Concentra el <strong>{stats.topCity.percentage}%</strong> de las visitas con <strong>{stats.topCity.count}</strong> accesos.
                  </p>
                </div>
              </div>
            )}

            {/* Lista de Ciudades con Barras de Proporción */}
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {stats.cityRanking.length === 0 || (stats.cityRanking.length === 1 && stats.cityRanking[0].cityName === 'Ubicación Desconocida' && stats.totalVisits === 0) ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  Sin visitas geolocalizadas aún.
                </div>
              ) : (
                stats.cityRanking.map((city, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-700 flex items-center gap-1.5 truncate">
                        <span className="text-sm">{getCountryFlag(city.countryCode)}</span>
                        <span className="truncate max-w-[240px]">
                          {city.cityName}
                          {city.region && city.cityName !== 'Ubicación Desconocida' && (
                            <span className="text-slate-400 text-[10px] font-normal ml-1">· {city.region}</span>
                          )}
                        </span>
                      </span>
                      <span className="font-mono font-bold text-slate-900 shrink-0">
                        {city.count} <span className="text-[10px] text-slate-400 font-normal">({city.percentage}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-cyan-600 rounded-full transition-all duration-500"
                        style={{ width: `${city.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between mt-3">
            <span>Detección no invasiva por IP (sin permisos invasivos de navegador)</span>
            <span className="font-medium text-slate-600">Total ubicaciones: {stats.cityRanking.reduce((acc, c) => acc + c.count, 0)}</span>
          </div>
        </div>

        {/* Alcance Internacional / Países (1 Columna) */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <Globe size={18} className="text-blue-600" />
                Países & Mercados
              </h3>
              <span className="text-xs text-slate-500">{stats.countryRanking.length} países</span>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {stats.countryRanking.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  Sin registros por país aún.
                </div>
              ) : (
                stats.countryRanking.map((country, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                        <span className="text-sm">{getCountryFlag(country.countryCode)}</span>
                        <span>{country.countryName}</span>
                      </span>
                      <span className="font-mono font-bold text-slate-900">
                        {country.count} <span className="text-[10px] text-slate-400 font-normal">({country.percentage}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 rounded-full transition-all duration-500"
                        style={{ width: `${country.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 mt-4 text-[11px] text-slate-500">
            <p className="flex items-center gap-1">
              <Compass size={14} className="text-slate-400" />
              <span>Detección de prospectos en México, Canadá, EE.UU., etc.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Bitácora en Tiempo Real de Visitas (con Métricas de Atención y Dwell Time) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <Clock size={18} className="text-cyan-600" />
              Bitácora en Tiempo Real de Visitas & Atención
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Rastreo exacto de canales, permanencia en sitio y módulo favorito de cada prospecto
            </p>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            <RefreshCw size={24} className="animate-spin mx-auto text-cyan-600 mb-2" />
            Cargando telemetría de visitas...
          </div>
        ) : filteredVisits.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            No se encontraron visitas con los filtros seleccionados.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[500px]">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider text-[10px] sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4">Fecha & Hora</th>
                  <th className="py-3 px-4">Ubicación / Ciudad</th>
                  <th className="py-3 px-4">Canal / Fuente</th>
                  <th className="py-3 px-4">Origen / Campaña</th>
                  <th className="py-3 px-4">Dispositivo</th>
                  <th className="py-3 px-4">Ruta / Páginas</th>
                  <th className="py-3 px-4">Permanencia</th>
                  <th className="py-3 px-4">Módulo Favorito</th>
                  <th className="py-3 px-4">Tipo / Audiencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredVisits.slice(0, 100).map((visit) => {
                  let formattedDate = visit.dateStr || '';
                  let formattedTime = '';
                  if (visit.timestamp?.toDate) {
                    const d = visit.timestamp.toDate();
                    formattedDate = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
                    formattedTime = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                  }

                  const dwellSec = visit.totalDwellSeconds || 0;
                  const favSecMeta = SECTION_METADATA[visit.favoriteSection || ''] || { label: visit.favoriteSection || 'hero', icon: '📌' };

                  return (
                    <tr key={visit.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Fecha & Hora */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="font-bold text-slate-800">{formattedDate}</span>
                        <span className="text-[11px] text-slate-400 ml-1.5 font-mono">{formattedTime}</span>
                      </td>

                      {/* Ubicación / Ciudad */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-slate-800">
                          <span className="text-base leading-none">{getCountryFlag(visit.countryCode)}</span>
                          <div>
                            <div className="flex items-center gap-1 font-bold text-xs text-slate-900">
                              <MapPin size={12} className="text-cyan-600 shrink-0" />
                              <span>{visit.city || 'Desconocida'}</span>
                            </div>
                            {(visit.region || visit.country) && (
                              <span className="text-[10px] text-slate-400 block font-normal">
                                {[visit.region, visit.country].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Canal */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        {getChannelBadge(visit.channel, visit.channelLabel)}
                      </td>

                      {/* Origen / Campaña */}
                      <td className="py-3 px-4">
                        {visit.utmCampaign ? (
                          <div className="space-y-0.5">
                            <span className="inline-block px-2 py-0.5 rounded bg-orange-50 text-orange-700 text-[10px] font-mono border border-orange-200">
                              {visit.utmCampaign}
                            </span>
                            {visit.utmSource && (
                              <p className="text-[10px] text-slate-400">src: {visit.utmSource}</p>
                            )}
                          </div>
                        ) : visit.utmSource ? (
                          <span className="inline-block px-2 py-0.5 rounded bg-cyan-50 text-cyan-800 text-[10px] font-mono border border-cyan-200">
                            {visit.utmSource}
                          </span>
                        ) : visit.referrerDomain ? (
                          <span className="text-slate-600 truncate block max-w-[150px]" title={visit.referrer}>
                            {visit.referrerDomain}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-[11px] italic">Directo</span>
                        )}
                      </td>

                      {/* Dispositivo */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-slate-700">
                          {visit.device === 'mobile' ? (
                            <Smartphone size={14} className="text-slate-400" />
                          ) : visit.device === 'tablet' ? (
                            <Tablet size={14} className="text-slate-400" />
                          ) : (
                            <Monitor size={14} className="text-slate-400" />
                          )}
                          <span>{visit.os}</span>
                          <span className="text-slate-400 text-[10px]">({visit.browser})</span>
                        </div>
                      </td>

                      {/* Ruta / Páginas Visitadas */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 flex-wrap max-w-[180px]">
                          {(visit.pagesVisited || [visit.landingPath || '/']).map((p, pIdx) => (
                            <span key={pIdx} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-700 border border-slate-200">
                              {p}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Permanencia (Dwell Time) */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                          dwellSec >= 120 
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : dwellSec >= 30
                            ? 'bg-purple-50 text-purple-800 border border-purple-200'
                            : 'bg-slate-100 text-slate-600 border border-slate-200'
                        }`}>
                          <Clock size={12} />
                          {formatDuration(dwellSec)}
                        </span>
                      </td>

                      {/* Módulo Favorito */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                          <span>{favSecMeta.icon}</span>
                          <span className="truncate max-w-[130px]">{favSecMeta.label.split(' ')[0]}</span>
                        </span>
                      </td>

                      {/* Tipo / Audiencia */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {visit.isTeam ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
                              <Shield size={10} className="text-purple-600" />
                              Equipo
                            </span>
                          ) : isBotVisit(visit) ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                              <Bot size={10} className="text-slate-500" />
                              Bot / Server
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <UserCheck size={10} className="text-emerald-600" />
                              Humano
                            </span>
                          )}

                          {visit.isNewVisitor ? (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-700">
                              Nuevo
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-indigo-50 text-indigo-700">
                              Recurrente
                            </span>
                          )}

                          {visit.isDev && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-amber-100 text-amber-800">
                              Dev
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
